/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { SubmitEvent, SyntheticEvent } from "react"
import { useState } from "react"
import { useParams } from "react-router"

import type { ApplicationType } from "../../features/application-type/application-type-types"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { valueOrDash } from "../../utils/value-or-dash"
import { useViewModel } from "../app/app-hooks"
import { AppIcon } from "../components/app-icon"
import { Button } from "../ui-widgets/button/button"
import { ApplicationTypeIconSelectView } from "./application-type-icon-select-view"

import "./application-type-detail-view.css"

export const ApplicationTypeDetailView = observer(() => {
    const { id } = useParams<{ id: string }>()

    const appTypeVM = useViewModel(ApplicationTypeViewModel)

    const appTypeId = Number(id)
    const appType = appTypeVM.appTypeFor(Number.isNaN(appTypeId) ? null : appTypeId)

    if (!appType) {
        return <ApplicationTypeNotFound />
    }

    // Remount the form when the route changes so its local edit state does not
    // carry over from the previous application type.
    return <ApplicationTypeDetailForm key={appTypeId} appTypeId={appTypeId} appType={appType} />
})

const ApplicationTypeNotFound = () => (
    <div className="page-container mt-6">
        <div className="alert alert-warning">Application not found.</div>
    </div>
)

interface Props {
    /**
     * ID from the route. Kept separate because the generated
     * `ApplicationType.id` field is optional.
     */
    appTypeId: number
    appType: ApplicationType
}

const ApplicationTypeDetailForm = observer(({ appTypeId, appType }: Props) => {
    // `null` means the field was not edited, so the value follows the store. Saving
    // resets it to `null` again, which picks up the record returned by the server.
    const [editedDescription, setEditedDescription] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    const [iconFile, setIconFile] = useState<File | null>(null)
    // Remount the icon picker to reset its internal preview after the selection is cleared.
    const [iconPickerKey, setIconPickerKey] = useState(0)

    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const orgVM = useViewModel(OrganizationViewModel)

    const orgName = orgVM.organizations.find((org) => org.id === appType.organizationId)?.name

    const storedDescription = appType.description ?? ""
    const description = editedDescription ?? storedDescription
    const trimmedDescription = description.trim()

    // Compare normalized values so whitespace in the stored value does not make the
    // form appear dirty when the user has not actually changed the description.
    const descriptionChanged = trimmedDescription !== storedDescription.trim()
    const isDirty = descriptionChanged || iconFile !== null
    const canSave = isDirty && trimmedDescription.length > 0

    // Reset the React state instead of relying on the native form reset: controlled
    // inputs ignore the native reset, and the icon picker needs a remount to clear
    // its internal preview.
    const handleReset = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()

        setEditedDescription(null)
        setIconFile(null)
        setIconPickerKey((key) => key + 1)
    }

    const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!canSave) {
            return
        }

        // The description and icon are saved independently. Clear each local edit only
        // after its request succeeds, so a partially failed save keeps the unsaved edit.
        // The service logs API errors.
        setSaving(true)

        try {
            if (descriptionChanged) {
                const result = await appTypeVM.updateAppType(appTypeId, trimmedDescription)
                if (!result.success) {
                    return
                }
                setEditedDescription(null)
            }

            if (iconFile) {
                const result = await appTypeVM.updateAppTypeIcon(appTypeId, iconFile)
                if (result.success) {
                    setIconFile(null)
                    setIconPickerKey((key) => key + 1)
                }
            }
        } finally {
            // Always re-enable the form, including when a request throws unexpectedly.
            setSaving(false)
        }
    }

    return (
        <div className="w-content col-xxl-6 col-xl-8 col-lg-10 col-md-12">
            <div className="application-type-detail-view card shadow-legacy-sm">
                <div className="card-header custom-card-header">
                    <h5 className="mb-0">Application Detail</h5>
                </div>
                <form onSubmit={handleSubmit} onReset={handleReset}>
                    <div className="card-body">
                        <table className="table table-borderless mb-0">
                            <tbody>
                                <tr>
                                    <th>ID:</th>
                                    <td>{appTypeId}</td>
                                </tr>
                                <tr>
                                    <th>
                                        <label htmlFor="description">Description:</label>
                                    </th>
                                    <td>
                                        <input
                                            type="text"
                                            value={description}
                                            className="form-control"
                                            id="description"
                                            disabled={saving}
                                            onChange={(e) => setEditedDescription(e.target.value)}
                                        />
                                    </td>
                                </tr>

                                <tr>
                                    <th>Package Name:</th>
                                    <td>{valueOrDash(appType.sourcePackageName)}</td>
                                </tr>
                                <tr>
                                    <th>Platform:</th>
                                    <td>{valueOrDash(appType.platform)}</td>
                                </tr>
                                <tr>
                                    <th>Category:</th>
                                    <td>{valueOrDash(appType.category)}</td>
                                </tr>
                                <tr>
                                    <th>Organization:</th>
                                    <td>{valueOrDash(orgName)}</td>
                                </tr>
                                <tr>
                                    <th className="text-right">
                                        <AppIcon appTypeId={appTypeId} />
                                    </th>
                                    <td>
                                        <ApplicationTypeIconSelectView key={iconPickerKey} onIconSelect={setIconFile} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer flex justify-end gap-2">
                        <Button type="reset" variant="secondary" disabled={!isDirty || saving}>
                            Discard
                        </Button>
                        <Button type="submit" busy={saving} disabled={!canSave}>
                            Save
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
})
